import { useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Users,
  MapPin,
  Building2,
  Layers,
  List,
  ChevronRight,
  FileSpreadsheet,
  Database,
  Bot,
  GitMerge
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface SettingTileProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  color: string;
}

const SettingTile = ({ icon, title, description, onClick, color }: SettingTileProps) => (
  <button
    onClick={onClick}
    className="flex items-start gap-3 p-4 bg-white rounded-xl border border-secondary-200 hover:border-primary-200 hover:shadow-lg hover:shadow-primary-50 transition-all text-left group relative overflow-hidden"
  >
    <div className={`absolute top-0 left-0 w-1 h-full ${color} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
    <div className={`p-2 rounded-lg ${color.replace('bg-', 'bg-').replace('500', '50')} ${color.replace('bg-', 'text-')} shrink-0`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="text-sm font-bold text-secondary-900 mb-0.5 group-hover:text-primary-600 transition-colors">
        {title}
      </h3>
      <p className="text-xs text-secondary-500 leading-relaxed line-clamp-2">
        {description}
      </p>
    </div>
    <ChevronRight className="text-secondary-300 group-hover:text-primary-400 transition-colors flex-shrink-0" size={16} />
  </button>
);

export default function Einstellungen() {
  const navigate = useNavigate();
  const { hatRecht } = useAuth();

  const stammdatenTiles = [
    {
      id: 'allgemeines',
      icon: <SettingsIcon size={18} />,
      title: 'Allgemeines',
      description: 'Firmendaten, Logo und Sachverständigen-Info',
      color: 'bg-primary-500'
    },
    {
      id: 'kunden',
      icon: <Users size={18} />,
      title: 'Kunden',
      description: 'Auftraggeber und Ansprechpartner',
      color: 'bg-blue-500'
    },
    {
      id: 'standorte',
      icon: <MapPin size={18} />,
      title: 'Standorte',
      description: 'Objekte und Adressen verwalten',
      color: 'bg-indigo-500'
    },
    {
      id: 'gebaeude',
      icon: <Building2 size={18} />,
      title: 'Gebäude',
      description: 'Gebäude pro Standort anlegen',
      color: 'bg-purple-500'
    },
    {
      id: 'stockwerke',
      icon: <Layers size={18} />,
      title: 'Stockwerke',
      description: 'Etagen und Ebenen verwalten',
      color: 'bg-fuchsia-500'
    },
    {
      id: 'katalog',
      icon: <List size={18} />,
      title: 'Kataloge',
      description: 'Textbausteine und Vorlagen',
      color: 'bg-pink-500'
    }
  ];

  const systemTiles = [
    {
      id: 'import-export',
      icon: <FileSpreadsheet size={18} />,
      title: 'Import/Export',
      description: 'Excel-Daten importieren und exportieren',
      color: 'bg-emerald-500'
    },
    {
      id: 'backup',
      icon: <Database size={18} />,
      title: 'Backup',
      description: 'Datenbank sichern und wiederherstellen',
      color: 'bg-amber-500'
    },
    {
      id: 'ki',
      icon: <Bot size={18} />,
      title: 'KI-Unterstützung',
      description: 'Nutzung, Obergrenze und Geheimnis-Maskierung',
      color: 'bg-cyan-500'
    },
    // Nur sichtbar mit dem Verwaltungsrecht -- ein Knopf, der beim
    // Klicken 403 liefert, ist schlechter als kein Knopf (Can.tsx).
    ...(hatRecht('system.sync_konflikte_verwalten')
      ? [
          {
            id: 'sync-konflikte',
            icon: <GitMerge size={18} />,
            title: 'Sync-Konflikte',
            description: 'Offene Konflikte aus der mobilen Erfassung, mandantenweit',
            color: 'bg-rose-500'
          }
        ]
      : [])
  ];

  return (
    <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
      <header className="mb-6">
        <h1 className="text-2xl font-black text-secondary-900 mb-1 tracking-tight">Einstellungen</h1>
        <p className="text-sm text-secondary-500">
          Stammdaten, Vorlagen und Systemfunktionen verwalten
        </p>
      </header>

      {/* Stammdaten Section */}
      <div className="mb-6">
        <h2 className="text-xs font-bold text-secondary-400 uppercase tracking-wider mb-3">Stammdaten</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {stammdatenTiles.map((tile) => (
            <SettingTile
              key={tile.id}
              {...tile}
              onClick={() => navigate(`/einstellungen/${tile.id}`)}
            />
          ))}
        </div>
      </div>

      {/* System Section */}
      <div>
        <h2 className="text-xs font-bold text-secondary-400 uppercase tracking-wider mb-3">System</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {systemTiles.map((tile) => (
            <SettingTile
              key={tile.id}
              {...tile}
              onClick={() => navigate(`/einstellungen/${tile.id}`)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
